import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { User, LoginCredentials, RegisterCredentials } from '@pms/shared';
import { getUser, setUser, setToken, clearToken, getRefreshToken } from '@/lib/auth';
import api from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  /** Set a real password for a pending (must-change) account; rotates tokens. */
  setPassword: (newPassword: string) => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(() => getUser());
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const { data } = await api.post<{ accessToken: string; refreshToken: string; user: User }>('/auth/login', credentials);
      setToken({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      setUser(data.user);
      setUserState(data.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    setIsLoading(true);
    try {
      const { data } = await api.post<{ accessToken: string; refreshToken: string; user: User }>('/auth/register', credentials);
      setToken({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      setUser(data.user);
      setUserState(data.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setPassword = useCallback(async (newPassword: string) => {
    setIsLoading(true);
    try {
      const { data } = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: User;
      }>('/auth/set-password', { newPassword });
      // Rotate to the fresh tokens (the temp-password token is now revoked) and
      // update the user (mustChangePassword now false).
      setToken({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      setUser(data.user);
      setUserState(data.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      const refreshToken = getRefreshToken();
      // Server revokes the refresh token; best-effort, always clear local state.
      await api.post('/auth/logout', { refreshToken }).catch(() => {
        /* ignore — local logout proceeds regardless */
      });
    } finally {
      clearToken();
      setUserState(null);
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, login, register, logout, setPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return ctx;
}
